import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Check, Shield, CreditCard, Calendar, Mail, Lock, User as UserIcon, MapPin, Building2, AlertCircle, Loader2 } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import api from '../lib/api';

// Países que ofrecemos en el dropdown (ES primero, luego resto UE, luego más frecuentes fuera UE)
const COUNTRIES = [
  { code: 'ES', name: 'España' },
  { code: 'FR', name: 'Francia' },
  { code: 'DE', name: 'Alemania' },
  { code: 'IT', name: 'Italia' },
  { code: 'PT', name: 'Portugal' },
  { code: 'NL', name: 'Países Bajos' },
  { code: 'BE', name: 'Bélgica' },
  { code: 'AT', name: 'Austria' },
  { code: 'IE', name: 'Irlanda' },
  { code: 'SE', name: 'Suecia' },
  { code: 'DK', name: 'Dinamarca' },
  { code: 'FI', name: 'Finlandia' },
  { code: 'PL', name: 'Polonia' },
  { code: 'GR', name: 'Grecia' },
  { code: 'CZ', name: 'Chequia' },
  { code: 'HU', name: 'Hungría' },
  { code: 'RO', name: 'Rumanía' },
  { code: 'HR', name: 'Croacia' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'SK', name: 'Eslovaquia' },
  { code: 'SI', name: 'Eslovenia' },
  { code: 'LT', name: 'Lituania' },
  { code: 'LV', name: 'Letonia' },
  { code: 'EE', name: 'Estonia' },
  { code: 'CY', name: 'Chipre' },
  { code: 'LU', name: 'Luxemburgo' },
  { code: 'MT', name: 'Malta' },
  { code: 'GB', name: 'Reino Unido' },
  { code: 'CH', name: 'Suiza' },
  { code: 'US', name: 'Estados Unidos' },
  { code: 'MX', name: 'México' },
  { code: 'AR', name: 'Argentina' },
  { code: 'CO', name: 'Colombia' },
  { code: 'CL', name: 'Chile' },
  { code: 'PE', name: 'Perú' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'BR', name: 'Brasil' },
  { code: 'CA', name: 'Canadá' },
  { code: 'AU', name: 'Australia' },
  { code: 'OTHER', name: 'Otro país' },
];

const emptyBilling = {
  legal_name: '',
  tax_id: '',
  address: '',
  postal_code: '',
  city: '',
  country: 'ES',
  is_business_with_vat_id: false,
  vat_id: '',
};

const Checkout = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get('mode') === 'installment' ? 'installment' : 'single';

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(initialMode);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Identity / auth inline
  const [email, setEmail] = useState('');
  const [emailChecked, setEmailChecked] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');

  // Billing
  const [billing, setBilling] = useState(emptyBilling);

  // Quote (VAT calculated in real time)
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  // Auth token after prepare (unlocks PayPal button)
  const [checkoutToken, setCheckoutToken] = useState(null);
  const [orderInfo, setOrderInfo] = useState(null); // { order_id | subscription_id }
  const [inlineError, setInlineError] = useState(null); // error visible arriba del botón

  const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || '';

  // --------- Load course ---------
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/courses/${courseId}`);
        setCourse(data);
      } catch (err) {
        toast.error('No hemos podido cargar el curso');
        navigate('/formaciones');
      } finally {
        setLoading(false);
      }
    })();
  }, [courseId, navigate]);

  // --------- Prefill from logged-in user (nice-to-have, they can still edit) ---------
  useEffect(() => {
    if (!localStorage.getItem('token')) return; // don't hit /auth/me for anonymous users
    (async () => {
      try {
        const { data } = await api.get('/auth/me');
        if (data?.email) {
          setEmail(data.email);
          setEmailExists(true);
          setEmailChecked(true);
        }
        if (data?.name) setName(data.name);
        if (data?.billing_profile) setBilling(b => ({ ...b, ...data.billing_profile }));
      } catch (_) { /* not logged in — ignore */ }
    })();
  }, []);

  // --------- Debounced email existence check ---------
  const emailTimer = useRef(null);
  const runEmailCheck = useCallback(async (value) => {
    if (!value || !/.+@.+\..+/.test(value)) {
      setEmailChecked(false);
      return;
    }
    setCheckingEmail(true);
    try {
      const { data } = await api.post('/checkout/check-email', { email: value });
      setEmailExists(!!data.exists);
      setEmailChecked(true);
      if (data.hint_name && !name) setName(data.hint_name);
    } catch (_) {
      setEmailChecked(false);
    } finally {
      setCheckingEmail(false);
    }
  }, [name]);

  useEffect(() => {
    if (emailTimer.current) clearTimeout(emailTimer.current);
    emailTimer.current = setTimeout(() => runEmailCheck(email.trim()), 500);
    return () => emailTimer.current && clearTimeout(emailTimer.current);
  }, [email, runEmailCheck]);

  // --------- Debounced VAT quote ---------
  const quoteTimer = useRef(null);
  useEffect(() => {
    if (!course) return;
    if (quoteTimer.current) clearTimeout(quoteTimer.current);
    quoteTimer.current = setTimeout(async () => {
      setQuoteLoading(true);
      try {
        const { data } = await api.post('/checkout/vat-quote', {
          course_id: courseId,
          mode,
          country: billing.country || '',
          postal_code: billing.postal_code || '',
          is_business_with_vat_id: !!billing.is_business_with_vat_id,
          vat_id: billing.vat_id || '',
        });
        setQuote(data);
      } catch (_) {
        setQuote(null);
      } finally {
        setQuoteLoading(false);
      }
    }, 300);
    return () => quoteTimer.current && clearTimeout(quoteTimer.current);
  }, [course, courseId, mode, billing.country, billing.postal_code, billing.is_business_with_vat_id, billing.vat_id]);

  // --------- Field validation ---------
  const validateIdentity = () => {
    if (!/.+@.+\..+/.test(email)) return 'Introduce un email válido';
    if (password.length < 8) return 'La contraseña debe tener al menos 8 caracteres';
    if (!emailExists) {
      if (!name.trim()) return 'Falta el nombre para crear tu cuenta';
      if (password !== confirmPassword) return 'Las contraseñas no coinciden';
    }
    return null;
  };
  const validateBilling = () => {
    const req = ['legal_name', 'tax_id', 'address', 'postal_code', 'city', 'country'];
    for (const k of req) if (!String(billing[k] || '').trim()) return `Rellena ${k.replace('_', ' ')}`;
    if (billing.is_business_with_vat_id && !billing.vat_id.trim()) return 'Introduce el NIF-IVA de tu empresa o desmarca la casilla';
    return null;
  };

  const formError = (() => {
    const ie = validateIdentity();
    if (ie) return ie;
    const be = validateBilling();
    if (be) return be;
    if (!termsAccepted) return 'Acepta las condiciones para continuar';
    return null;
  })();

  // --------- Prepare (create/login user + save billing) ---------
  const prepareCheckout = async () => {
    const err = formError;
    if (err) { toast.error(err); throw new Error(err); }
    const { data } = await api.post('/checkout/prepare', {
      course_id: courseId,
      mode,
      email: email.trim(),
      password,
      name: name.trim(),
      billing,
    });
    setCheckoutToken(data.access_token);
    // Store token so subsequent api calls use it (interceptor reads from localStorage)
    localStorage.setItem('token', data.access_token);
    return data;
  };

  const handleContinueToPayment = async () => {
    setInlineError(null);
    try {
      setProcessing(true);
      await prepareCheckout();
      toast.success('Datos guardados. Pulsa el botón de PayPal para finalizar.');
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'No se pudo continuar';
      console.error('[Checkout] handleContinueToPayment error:', err.response?.data || err);
      setInlineError(detail);
      toast.error(detail);
    } finally {
      setProcessing(false);
    }
  };

  // --------- PayPal handlers — sin setState para no destruir el widget ---------
  const onCreateOrder = async () => {
    try {
      const { data } = await api.post('/checkout/create-payment', { course_id: courseId, mode: 'single' });
      if (!data.order_id) throw new Error('No order_id received');
      return data.order_id;
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'No se pudo iniciar el pago';
      console.error('[Checkout] onCreateOrder error:', err.response?.data || err);
      setInlineError(detail);
      toast.error(detail);
      throw err;
    }
  };

  const onApproveOrder = async (pp) => {
    try {
      const { data } = await api.post('/checkout/complete', {
        course_id: courseId, mode: 'single', order_id: pp.orderID,
      });
      toast.success('¡Pago confirmado! Te llevamos a tu panel de alumna.');
      navigate(data.redirect || '/mi-camino');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error al confirmar el pago.';
      console.error('[Checkout] onApproveOrder error:', err.response?.data || err);
      setInlineError(detail);
      toast.error(detail);
    }
  };

  const onCreateSubscription = async () => {
    try {
      const { data } = await api.post('/checkout/create-payment', { course_id: courseId, mode: 'installment' });
      if (!data.subscription_id) throw new Error('No subscription_id received');
      return data.subscription_id;
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'No se pudo iniciar la suscripción';
      console.error('[Checkout] onCreateSubscription error:', err.response?.data || err);
      setInlineError(detail);
      toast.error(detail);
      throw err;
    }
  };

  const onApproveSubscription = async (pp) => {
    try {
      const { data } = await api.post('/checkout/complete', {
        course_id: courseId, mode: 'installment', subscription_id: pp.subscriptionID,
      });
      toast.success('¡Primera cuota pagada! Las siguientes se cobrarán automáticamente.');
      navigate(data.redirect || '/mi-camino');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error al confirmar la suscripción.';
      console.error('[Checkout] onApproveSubscription error:', err.response?.data || err);
      setInlineError(detail);
      toast.error(detail);
    }
  };

  const onPayPalError = (err) => {
    console.error('[Checkout] PayPal SDK error:', err);
    const msg = err?.message || String(err) || 'Error con PayPal';
    setInlineError(`PayPal: ${msg}`);
    toast.error('Error con PayPal. Revisa el mensaje o escríbenos a hola@herminiovazquez.com.');
  };

  // ==================== RENDER ====================
  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[hsl(var(--accent))] animate-spin" />
      </div>
    );
  }
  if (!course) return null;

  const installmentsAvailable = course?.installments_enabled;
  const installmentCycles = course?.installment_cycles || 2;
  const currency = '€';

  return (
    <div className="pt-20 min-h-screen bg-[hsl(var(--sage-light))]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-12">
        <Link
          to={`/formaciones/${course.slug || courseId}`}
          className="inline-flex items-center gap-2 text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))] mb-6 sm:mb-8 transition-colors"
          data-testid="checkout-back"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          Volver al curso
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
          {/* ===================== LEFT: ORDER SUMMARY ===================== */}
          <aside className="lg:col-span-2">
            <div className="card-soft lg:sticky lg:top-28">
              <h2 className="text-xl font-heading text-[hsl(var(--text-primary))] mb-6">Resumen del pedido</h2>

              <div className="flex gap-4 mb-6 pb-6 border-b border-[hsl(var(--border))]">
                <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-[hsl(var(--sage-light))]">
                  {course.image_url && (
                    <img src={course.image_url} alt={course.title} className="w-full h-full object-cover" />
                  )}
                </div>
                <div>
                  <h3 className="font-medium text-[hsl(var(--text-primary))] mb-1" data-testid="checkout-course-title">
                    {course.title}
                  </h3>
                  <p className="text-sm text-[hsl(var(--text-secondary))]">
                    {course.duration || course.subtitle}
                  </p>
                </div>
              </div>

              {/* Live totals from /checkout/vat-quote */}
              <div className="space-y-2 mb-4 text-sm">
                {mode === 'single' ? (
                  <>
                    <Row label="Precio base" value={fmt(quote?.total_net ?? course.price)} />
                    <Row label={`IVA ${quote?.vat_rate ?? '—'}%`} value={fmt(quote?.total_vat)} muted />
                  </>
                ) : (
                  <>
                    <Row
                      label={`Cuota hoy (1/${installmentCycles})`}
                      value={fmt(quote?.per_cycle_total ?? (course.installment_price || 0))}
                    />
                    {Array.from({ length: Math.max(0, installmentCycles - 1) }).map((_, i) => (
                      <Row
                        key={i}
                        label={`Cuota ${i + 2}/${installmentCycles} · a ${(i + 1) * 30} días`}
                        value={fmt(quote?.per_cycle_total ?? (course.installment_price || 0))}
                        muted
                      />
                    ))}
                    <p className="text-[11px] text-[hsl(var(--text-tertiary))] pt-1">
                      Las cuotas se cobran automáticamente cada 30 días.
                    </p>
                  </>
                )}
              </div>

              <div className="flex justify-between text-lg font-heading text-[hsl(var(--text-primary))] pt-4 border-t border-[hsl(var(--border))]">
                <span>Total {mode === 'installment' ? '(todas las cuotas)' : ''}</span>
                <span className="text-[hsl(var(--accent))]" data-testid="checkout-total">
                  {quoteLoading ? <Loader2 className="inline w-4 h-4 animate-spin" /> : fmt(quote?.total)}
                </span>
              </div>

              {quote?.legal_note && (
                <p className="text-[11px] text-[hsl(var(--text-tertiary))] leading-relaxed mt-4 p-3 bg-[hsl(var(--sage-light))]/40 rounded-lg" data-testid="checkout-vat-note">
                  <strong>Impuestos ({quote?.country_name || '—'}):</strong> {quote.legal_note}
                </p>
              )}
            </div>
          </aside>

          {/* ===================== RIGHT: FORM ===================== */}
          <section className="lg:col-span-3 space-y-6">

            {/* Mode switcher */}
            {installmentsAvailable && (
              <div className="card-soft">
                <p className="text-sm text-[hsl(var(--text-tertiary))] mb-3">¿Cómo quieres pagar?</p>
                <div className="grid grid-cols-2 gap-3">
                  <ModePill active={mode === 'single'} onClick={() => setMode('single')} icon={CreditCard}
                            title="Pago único" subtitle={fmt(course.price) + ' base · IVA se calcula abajo'} testid="checkout-mode-single" />
                  <ModePill active={mode === 'installment'} onClick={() => setMode('installment')} icon={Calendar}
                            title={`${installmentCycles} cuotas`} subtitle={`${installmentCycles} × ${fmt(course.installment_price)} + IVA`} testid="checkout-mode-installment" />
                </div>
              </div>
            )}

            {/* 1. Identity */}
            <div className="card-soft">
              <SectionHeader step="1" icon={Mail} title="Tu correo" />
              <FieldRow>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@correo.com"
                  className="input-akashica w-full"
                  data-testid="checkout-email"
                />
              </FieldRow>
              {emailChecked && !checkingEmail && (
                <p className={`text-xs mt-2 ${emailExists ? 'text-[hsl(var(--sage))]' : 'text-[#8a7428]'}`} data-testid="checkout-email-status">
                  {emailExists ? 'Ya tienes cuenta — introduce tu contraseña.' : 'Vamos a crear tu cuenta ahora mismo.'}
                </p>
              )}

              {emailChecked && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                  {!emailExists && (
                    <FieldRow className="sm:col-span-2">
                      <Label>Tu nombre</Label>
                      <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                             placeholder="Nombre y apellido" className="input-akashica w-full"
                             data-testid="checkout-name" />
                    </FieldRow>
                  )}
                  <FieldRow>
                    <Label icon={Lock}>{emailExists ? 'Tu contraseña' : 'Crea tu contraseña (min. 8)'}</Label>
                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                           placeholder="••••••••" className="input-akashica w-full"
                           data-testid="checkout-password" />
                  </FieldRow>
                  {!emailExists && (
                    <FieldRow>
                      <Label>Confirmar contraseña</Label>
                      <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                             placeholder="••••••••" className="input-akashica w-full"
                             data-testid="checkout-password-confirm" />
                    </FieldRow>
                  )}
                </div>
              )}
            </div>

            {/* 2. Billing */}
            <div className="card-soft">
              <SectionHeader step="2" icon={MapPin} title="Datos de facturación" />
              <p className="text-xs text-[hsl(var(--text-tertiary))] mb-4">
                Necesarios para emitir tu factura con el IVA correspondiente a tu país.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FieldRow className="sm:col-span-2">
                  <Label>Nombre completo o razón social</Label>
                  <input type="text" value={billing.legal_name}
                         onChange={(e) => setBilling(b => ({ ...b, legal_name: e.target.value }))}
                         className="input-akashica w-full" data-testid="checkout-legal-name" />
                </FieldRow>
                <FieldRow>
                  <Label>NIF / DNI / NIE / pasaporte</Label>
                  <input type="text" value={billing.tax_id}
                         onChange={(e) => setBilling(b => ({ ...b, tax_id: e.target.value.toUpperCase() }))}
                         className="input-akashica w-full" data-testid="checkout-tax-id" />
                </FieldRow>
                <FieldRow>
                  <Label>País</Label>
                  <select value={billing.country}
                          onChange={(e) => setBilling(b => ({ ...b, country: e.target.value }))}
                          className="input-akashica w-full" data-testid="checkout-country">
                    {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                  </select>
                </FieldRow>
                <FieldRow className="sm:col-span-2">
                  <Label>Dirección</Label>
                  <input type="text" value={billing.address}
                         onChange={(e) => setBilling(b => ({ ...b, address: e.target.value }))}
                         placeholder="Calle, número, piso" className="input-akashica w-full"
                         data-testid="checkout-address" />
                </FieldRow>
                <FieldRow>
                  <Label>Código postal</Label>
                  <input type="text" value={billing.postal_code}
                         onChange={(e) => setBilling(b => ({ ...b, postal_code: e.target.value }))}
                         className="input-akashica w-full" data-testid="checkout-postal-code" />
                </FieldRow>
                <FieldRow>
                  <Label>Ciudad</Label>
                  <input type="text" value={billing.city}
                         onChange={(e) => setBilling(b => ({ ...b, city: e.target.value }))}
                         className="input-akashica w-full" data-testid="checkout-city" />
                </FieldRow>
              </div>

              {/* EU business toggle */}
              <div className="mt-4 p-3 rounded-xl bg-[hsl(var(--sage-light))]/30 border border-[hsl(var(--border))]">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={billing.is_business_with_vat_id}
                            onCheckedChange={(v) => setBilling(b => ({ ...b, is_business_with_vat_id: !!v }))}
                            className="mt-0.5" data-testid="checkout-is-business" />
                  <div className="flex-1">
                    <span className="text-sm text-[hsl(var(--text-primary))] font-medium flex items-center gap-2">
                      <Building2 className="w-4 h-4" strokeWidth={1.5} /> Soy empresa UE con NIF-IVA válido
                    </span>
                    <p className="text-[11px] text-[hsl(var(--text-tertiary))] mt-1">
                      Marca esta casilla si tu empresa está en otro país UE y quieres aplicar la inversión del sujeto pasivo (0% IVA). Verificaré tu NIF-IVA manualmente.
                    </p>
                  </div>
                </label>
                {billing.is_business_with_vat_id && (
                  <div className="mt-3">
                    <Label>NIF-IVA intracomunitario (con prefijo, ej. FR12345678901)</Label>
                    <input type="text" value={billing.vat_id}
                           onChange={(e) => setBilling(b => ({ ...b, vat_id: e.target.value.toUpperCase() }))}
                           className="input-akashica w-full" data-testid="checkout-vat-id" />
                  </div>
                )}
              </div>
            </div>

            {/* 3. Legal & PayPal */}
            <div className="card-soft">
              <SectionHeader step="3" icon={Shield} title="Confirmar y pagar" />

              <div className="mb-4 p-4 rounded-xl bg-[#faf6ed] border border-[#e8d9a8]" data-testid="checkout-legal-block">
                <p className="text-xs text-[hsl(var(--text-secondary))] leading-relaxed">
                  <strong>Información precontractual:</strong> reservas plaza en <strong>{course.title}</strong>,
                  formación online en directo con fechas concretas.
                  Importe total: <strong>{fmt(quote?.total)} {mode === 'installment' && `(en ${installmentCycles} cuotas de ${fmt(quote?.per_cycle_total)})`}</strong>.
                  El acceso al área de alumna se habilita ahora (curso marcado como &ldquo;Próximamente&rdquo;) y el servicio se inicia el día de la primera sesión en directo.
                </p>
                <p className="text-[11px] text-[hsl(var(--text-tertiary))] leading-relaxed mt-2">
                  Al tratarse de un servicio con fecha concreta (art. 103.l TRLGDCU), no aplica el desistimiento legal de 14 días. La escuela ofrece voluntariamente una <strong>garantía de satisfacción de 14 días</strong>: escríbenos a hola@herminiovazquez.com y te devolvemos el importe íntegro.
                </p>
              </div>

              <div className="flex items-start gap-3 mb-4">
                <Checkbox id="accept-terms" checked={termsAccepted}
                          onCheckedChange={setTermsAccepted}
                          className="mt-0.5 border-[#c9a227]" data-testid="checkout-terms" />
                <label htmlFor="accept-terms" className="text-xs text-[hsl(var(--text-secondary))] leading-relaxed cursor-pointer">
                  He leído y acepto los{' '}
                  <Link to="/terminos" target="_blank" className="text-[#c9a227] hover:underline">Términos y condiciones</Link>
                  {' '}y la{' '}
                  <Link to="/privacidad" target="_blank" className="text-[#c9a227] hover:underline">Política de privacidad</Link>.
                </label>
              </div>

              {formError && (
                <p className="text-xs text-[#b45a30] mb-3 text-center font-medium flex items-center justify-center gap-2" data-testid="checkout-form-error">
                  <AlertCircle className="w-3.5 h-3.5" /> {formError}
                </p>
              )}

              {inlineError && (
                <div className="mb-4 p-3 rounded-lg bg-[#fff2ed] border border-[#f5c8a8] text-[#993d0f] text-sm flex items-start gap-2" data-testid="checkout-inline-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={1.75} />
                  <span>{inlineError}</span>
                </div>
              )}

              <div className={processing ? 'pointer-events-none opacity-50' : ''}>
                {!PAYPAL_CLIENT_ID ? (
                  <div className="p-4 rounded-xl bg-[hsl(var(--sage-light))] text-center">
                    <p className="text-sm text-[hsl(var(--text-secondary))]">
                      PayPal no está configurado. Escríbenos a hola@herminiovazquez.com.
                    </p>
                  </div>
                ) : !checkoutToken ? (
                  // Paso 1: Validar + preparar (crea user + guarda billing).
                  // Solo entonces se monta PayPalScriptProvider para evitar zoid destroy.
                  <button
                    type="button"
                    onClick={handleContinueToPayment}
                    disabled={!!formError || processing}
                    className="w-full h-12 rounded-full bg-[#c9a227] hover:bg-[#b8921f] text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="checkout-continue-btn"
                  >
                    {processing ? 'Preparando…' : `Continuar al pago · ${fmt(quote?.total)}`}
                  </button>
                ) : mode === 'single' ? (
                  <PayPalScriptProvider
                    key={`single-${courseId}`}
                    options={{ clientId: PAYPAL_CLIENT_ID, currency: 'EUR', intent: 'capture', dataNamespace: 'paypal_single_v2' }}
                  >
                    <PayPalButtons
                      style={{ layout: 'vertical', shape: 'pill', label: 'paypal' }}
                      createOrder={onCreateOrder}
                      onApprove={onApproveOrder}
                      onError={onPayPalError}
                    />
                  </PayPalScriptProvider>
                ) : (
                  <PayPalScriptProvider
                    key={`installment-${courseId}`}
                    options={{ clientId: PAYPAL_CLIENT_ID, currency: 'EUR', vault: true, intent: 'subscription', dataNamespace: 'paypal_subs_v2' }}
                  >
                    <PayPalButtons
                      style={{ layout: 'vertical', shape: 'pill', label: 'subscribe' }}
                      createSubscription={onCreateSubscription}
                      onApprove={onApproveSubscription}
                      onError={onPayPalError}
                    />
                  </PayPalScriptProvider>
                )}
                {checkoutToken && (
                  <p className="text-[11px] text-[hsl(var(--text-tertiary))] text-center mt-3">
                    Tus datos ya están guardados. Pulsa PayPal arriba para completar el pago.
                  </p>
                )}
              </div>

              <div className="mt-6 pt-6 border-t border-[hsl(var(--border))]">
                <div className="flex items-start gap-3 mb-3">
                  <Shield className="w-5 h-5 text-[hsl(var(--sage))] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="text-sm font-medium text-[hsl(var(--text-primary))] mb-1">Pago 100% seguro</p>
                    <p className="text-xs text-[hsl(var(--text-tertiary))]">Encriptación SSL · PayPal Verified</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[hsl(var(--sage))] flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <div>
                    <p className="text-sm font-medium text-[hsl(var(--text-primary))]">Garantía de 14 días</p>
                    <p className="text-xs text-[hsl(var(--text-secondary))]">Devolución íntegra si no es para ti.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

// ==================== Helpers ====================
const fmt = (v) => `${Number(v || 0).toFixed(2).replace(/\.00$/, '')}€`;

const Row = ({ label, value, muted }) => (
  <div className={`flex justify-between ${muted ? 'text-[hsl(var(--text-tertiary))]' : 'text-[hsl(var(--text-secondary))]'}`}>
    <span>{label}</span>
    <span>{value}</span>
  </div>
);

const SectionHeader = ({ step, icon: Icon, title }) => (
  <div className="flex items-center gap-3 mb-4">
    <span className="w-7 h-7 rounded-full bg-[hsl(var(--accent))]/15 border border-[hsl(var(--accent))]/40 text-[hsl(var(--accent))] flex items-center justify-center text-xs font-medium">
      {step}
    </span>
    <h2 className="text-lg font-heading text-[hsl(var(--text-primary))] flex items-center gap-2">
      {Icon && <Icon className="w-4 h-4" strokeWidth={1.5} />} {title}
    </h2>
  </div>
);

const FieldRow = ({ children, className = '' }) => <div className={className}>{children}</div>;
const Label = ({ children, icon: Icon }) => (
  <label className="text-xs text-[hsl(var(--text-tertiary))] mb-1 block flex items-center gap-1.5">
    {Icon && <Icon className="w-3 h-3" strokeWidth={1.5} />} {children}
  </label>
);

const ModePill = ({ active, onClick, icon: Icon, title, subtitle, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`flex flex-col items-start gap-1 p-4 rounded-xl border-2 transition-colors text-left ${
      active ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5' : 'border-[hsl(var(--border))] hover:border-[hsl(var(--accent))]/40'
    }`}>
    <div className="flex items-center gap-2 text-sm font-medium text-[hsl(var(--text-primary))]">
      <Icon className="w-4 h-4" strokeWidth={1.5} /> {title}
    </div>
    <p className="text-xs text-[hsl(var(--text-tertiary))]">{subtitle}</p>
  </button>
);

export default Checkout;
